import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiClock, FiUsers } = FiIcons;

const CustomerStories = () => {
  const stories = [
    {
      company: 'TechFlow Startup',
      industry: 'Technology',
      logo: 'TF',
      challenge: 'Needed to hire 50 engineers in 6 months',
      result: 'Reduced time-to-hire by 65%',
      metrics: [
        { label: 'Time saved', value: '40 hours/week' },
        { label: 'Hire rate', value: '3x faster' },
        { label: 'Candidate satisfaction', value: '94%' }
      ],
      quote: "Instashortlist transformed our hiring process. We went from spending weeks on manual screening to making quality hires in days.",
      author: "Sarah Chen, Head of Talent"
    },
    {
      company: 'RetailMax Chain',
      industry: 'Retail',
      logo: 'RM',
      challenge: 'Seasonal hiring for 200+ store positions',
      result: 'Increased interview completion by 78%',
      metrics: [
        { label: 'Applications processed', value: '5,000+' },
        { label: 'Interview completion', value: '+78%' },
        { label: 'Cost per hire', value: '-45%' }
      ],
      quote: "The mobile-first interview process was a game-changer for our hourly workers. Much higher completion rates.",
      author: "Marcus Rodriguez, Talent Director"
    },
    {
      company: 'FinSecure Bank',
      industry: 'Financial Services',
      logo: 'FS',
      challenge: 'Compliance-heavy hiring with audit requirements',
      result: 'Improved hiring consistency by 85%',
      metrics: [
        { label: 'Compliance score', value: '99.8%' },
        { label: 'Panel consensus', value: '+85%' },
        { label: 'Audit ready', value: '100%' }
      ],
      quote: "The transparency and audit trail features ensure we meet all regulatory requirements while hiring faster.",
      author: "Jennifer Park, Compliance Lead"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-slate-900 mb-6">
            Success Stories Across Industries
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            See how companies are transforming their hiring with Instashortlist
          </p>
        </motion.div>

        <div className="space-y-12">
          {stories.map((story, index) => (
            <motion.div
              key={index}
              className="bg-slate-50 rounded-3xl p-8 lg:p-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                {/* Company Info */}
                <div className="text-center lg:text-left">
                  <div className="flex items-center justify-center lg:justify-start space-x-4 mb-4">
                    <div className="w-16 h-16 bg-indigo-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{story.logo}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl text-slate-900">{story.company}</h3>
                      <p className="text-slate-600">{story.industry}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-medium text-slate-900 mb-2">Challenge</h4>
                      <p className="text-slate-600 text-sm">{story.challenge}</p>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                      <h4 className="font-medium text-green-900 mb-2">Result</h4>
                      <p className="text-green-700 text-sm font-medium">{story.result}</p>
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
                  {story.metrics.map((metric, metricIndex) => (
                    <div key={metricIndex} className="bg-white rounded-xl p-6 text-center shadow-elegant">
                      <div className="text-2xl lg:text-3xl font-bold text-indigo-600 mb-2">
                        {metric.value}
                      </div>
                      <div className="text-slate-600 text-sm font-medium">
                        {metric.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Testimonial */}
                <div className="bg-white rounded-2xl p-6 shadow-elegant">
                  <blockquote className="text-lg text-slate-700 italic mb-4">
                    "{story.quote}"
                  </blockquote>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {story.author.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{story.author}</div>
                      <div className="text-sm text-slate-500">{story.company}</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CustomerStories;